import React, { createContext, useState, useEffect, useCallback } from "react"; // Added useCallback
import useLocalStorage from "../hooks/useLocalStorage";
import axios from "axios"; // Import axios
import { toast } from "../components/ui/use-toast"; // Import toast for notifications
import { useAuth } from "./AuthContext"; // Import useAuth to get user info

export const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const { user } = useAuth(); // Get authenticated user info
  const API_BASE_URL = process.env.REACT_APP_API_URL || "https://isco-backend-deployment-932661235642.us-central1.run.app";
  // Use state instead of localStorage for large cluster data to avoid quota issues
  const [data, setData] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false); // Loading state for feedback submission
  const [feedbackError, setFeedbackError] = useState(null); // Error state for feedback submission

  const [selectedClusterId, setSelectedClusterId] = useLocalStorage(
    "isco_selectedClusterId",
    null,
  );
  const [uniqueClusters, setUniqueClusters] = useState([]);
  const nullSelectedCluster = "Select a schedule";

  // Feedback states (using localStorage - keep as is for now)
  const [clusterGroupingsFeedback, setClusterGroupingsFeedback] =
    useLocalStorage("isco_clusterGroupingsFeedback", null);
  const [directionsFeedback, setDirectionsFeedback] = useLocalStorage(
    "isco_directionsFeedback",
    null,
  );
  const [customerStopsFeedback, setCustomerStopsFeedback] = useLocalStorage(
    "isco_customerStopsFeedback",
    null,
  );
  const [itineraryFeedback, setItineraryFeedback] = useLocalStorage(
    "isco_itineraryFeedback",
    null,
  );
  const [reasoningFeedback, setReasoningFeedback] = useLocalStorage(
    "isco_reasoningFeedback",
    null,
  );

  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (Array.isArray(data) && data.length > 0) {
      const clusters = data.map((item) => String(item.cluster));
      const uniqueSet = [...new Set(clusters)];

      if (JSON.stringify(uniqueSet) !== JSON.stringify(uniqueClusters)) {
        setUniqueClusters(uniqueSet);
      }

      if (selectedClusterId === null && clusters.length > 0) {
        setSelectedClusterId(clusters[0]);
      }
    }
  }, [data, selectedClusterId, setSelectedClusterId, uniqueClusters]); // Added dependencies

  // FEEDBACK submission function
  const submitFeedback = useCallback(
    async (feedbackType, feedbackData, context = {}) => {
      // Ensure feedbackData is not null before proceeding
      if (!feedbackData) {
        console.warn(
          "Attempted to submit null feedback for type:",
          feedbackType,
        );
        // Optionally, show a toast or handle this case specifically
        // toast({ title: "Info", description: "No feedback data to submit." });
        return; // Don't proceed if feedbackData is null
      }

      setFeedbackLoading(true);
      setFeedbackError(null);
      try {
        const payload = {
          feedback_type: feedbackType,
          feedback_flag: feedbackData.feedbackFlag,
          feedback_text: feedbackData.feedbackText || null, // Send null if empty string
          ratings: feedbackData.ratings || null, // Send null if no ratings object
          context: {
            // Add relevant context
            page: window.location.hash.replace('#', '') || '/',
            schedule_id: context.scheduleId || null,
            user_role: user?.role || null,
          },
          // user_id will be derived from the auth token on the backend
        };

        console.log("Submitting feedback:", payload); // Log payload

        // Use the BASE_URL defined earlier
        await axios.post(`${API_BASE_URL}/feedback`, payload);

        toast({
          title: "Feedback Submitted",
          description: "Thank you for your input!",
        });

        // Optionally clear the specific feedback state after successful submission
        // This depends on whether you want the UI to reset immediately
        // switch (feedbackType) {
        //   case 'cluster-groupings': setClusterGroupingsFeedback(null); break;
        //   case 'directions': setDirectionsFeedback(null); break;
        //   // ... add cases for other feedback types
        // }
      } catch (error) {
        console.error("Error submitting feedback:", error);
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Failed to submit feedback.";
        setFeedbackError(errorMessage);
        toast({
          title: "Feedback Submission Failed",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setFeedbackLoading(false);
      }
    },
    [
      API_BASE_URL,
      user, // Add dependencies
      // Include feedback state setters if you decide to clear state on success
      // setClusterGroupingsFeedback, setDirectionsFeedback, ...
    ],
  );

  // Original logFeedback function (can be removed or repurposed if API handles logging)
  const logFeedback = async (feedbackType, feedbackData) => {
    // This function can be deprecated if submitFeedback covers all needs
    console.log("Logging feedback (local):", feedbackType, feedbackData);
    // If you still need local logging unrelated to API submission, keep it here.
  };

  useEffect(() => {
    if (clusterGroupingsFeedback !== null) {
      // Decide if you want to automatically submit when localStorage changes,
      // or only when the user clicks "Submit" in the component.
      // Current implementation relies on the component's submit button.
      // logFeedback("cluster-groupings", clusterGroupingsFeedback);
    }
  }, [clusterGroupingsFeedback]);

  // Similar useEffects for other feedback types can be removed if submission
  // is handled exclusively by the submitFeedback function via button click.

  return (
    <AppContext.Provider
      value={{
        API_BASE_URL,
        data,
        setData,
        error,
        setError,
        loading,
        setLoading,
        feedbackLoading, // Expose feedback loading state
        feedbackError, // Expose feedback error state
        submitFeedback, // Expose the submission function
        selectedClusterId,
        setSelectedClusterId,
        uniqueClusters,
        setUniqueClusters,
        nullSelectedCluster,
        // Keep existing feedback states exposed for FeedbackComponent interaction
        clusterGroupingsFeedback,
        setClusterGroupingsFeedback,
        directionsFeedback,
        setDirectionsFeedback,
        customerStopsFeedback,
        setCustomerStopsFeedback,
        itineraryFeedback,
        setItineraryFeedback,
        reasoningFeedback,
        setReasoningFeedback,
        filterOptions,
        setFilterOptions,
        logFeedback, // Keep if still needed for other purposes
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
